import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { DocumentTemplate, DocumentType } from '../../../../../../../models/document-template';
import { Proficiency } from '../../../../../../../models/proficiency';
import { ProficiencyService } from '../../../../../../../services/proficiency/proficiency.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { SelectLocationModalComponent } from '../../../../modals/select-location-modal/select-location-modal.component';
import { CapitanLocation } from '../../../../../../../models/location';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  PreviewDocumentEmailModalComponent
} from '../modals/preview-document-email-modal/preview-document-email-modal.component';

@Component({
  selector: 'app-document-template-edit-settings',
  templateUrl: './document-template-edit-settings.component.html',
  styleUrls: ['./document-template-edit-settings.component.css']
})
export class DocumentTemplateEditSettingsComponent implements OnInit {

  public proficiencyList: Proficiency[];

  public loadingProficiencies = false;
  public serverError: number = null;

  @Input()
  documentTemplate: DocumentTemplate;

  @Input()
  documentTemplateErrors: any;

  @Output()
  documentTemplateChange = new EventEmitter<DocumentTemplate>();

  @Output()
  unsavedChanges = new EventEmitter<void>();

  public DocumentType = DocumentType;

  constructor(
    private accountService: AccountService,
    private proficiencyService: ProficiencyService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadProficiencies();
  }

  public toggleItemInList(thisItem: number, thisList: number[]) {
    if (thisList.includes(thisItem)) {
      thisList.splice(thisList.indexOf(thisItem), 1);
    } else {
      thisList.push(thisItem);
    }

    this.unsavedChanges.emit();
  }

  public openAddAllowedLocationModal() {
    const modalRef = this.modalService.open(SelectLocationModalComponent);
    modalRef.componentInstance.excludeIds = this.documentTemplate
      .allowed_locations.map(loc => loc.id);
    modalRef.componentInstance.submitCallback = (result: CapitanLocation) => {
      this.documentTemplate.allowed_locations.push({
        id: result.id,
        name: result.name
      });
      this.unsavedChanges.emit();
    }
  }

  public documentExpires() {
    return !!(
      (this.documentTemplate.years_to_expiration > 0) ||
      (this.documentTemplate.days_to_expiration > 0) ||
      this.documentTemplate.expire_when_minor_becomes_adult
    );
  }

  public leadDaysInvalid() {
    const leadDays = this.documentTemplate.expiring_soon_email_lead_days;
    return !(
      Number.isInteger(leadDays) &&
      (leadDays >= 1) &&
      (leadDays <= 365)
    );
  }

  public openExpiringSoonEmailPreviewModal() {
    const modalRef = this.modalService.open(
      PreviewDocumentEmailModalComponent,
      {
        size: 'lg',
      }
    );

    modalRef.componentInstance.documentTemplateId = this.documentTemplate.id;
    modalRef.componentInstance.emailKind = 'expiring_soon';
    modalRef.componentInstance.text = this.documentTemplate.expiring_soon_email_text;
    modalRef.componentInstance.includeButton = this.documentTemplate.expiring_soon_email_include_button;
    modalRef.componentInstance.leadDays = this.documentTemplate.expiring_soon_email_lead_days;
    modalRef.componentInstance.buttonText = this.documentTemplate.expiring_soon_email_button_text;
  }

  public openExpiredEmailPreviewModal() {
    const modalRef = this.modalService.open(
      PreviewDocumentEmailModalComponent,
      {
        size: 'lg',
      }
    );

    modalRef.componentInstance.documentTemplateId = this.documentTemplate.id;
    modalRef.componentInstance.emailKind = 'expired';
    modalRef.componentInstance.text = this.documentTemplate.expired_email_text;
    modalRef.componentInstance.includeButton = this.documentTemplate.expired_email_include_button;
    modalRef.componentInstance.buttonText = this.documentTemplate.expired_email_button_text;
  }

  public documentHasWaiverFunction() {
    return (
      (this.documentTemplate.document_type === DocumentType.waiver) ||
      (
        (this.documentTemplate.document_type === DocumentType.supervisorForm) &&
        this.documentTemplate.is_available_to_supervisors_with_supervisees
      )
    );
  }

  private loadProficiencies() {
    this.loadingProficiencies = true;

    this.proficiencyService
      .getProficiencies(1, 100)
      .then(response => {
        this.proficiencyList = response.results;

        this.removeUnknownGrantedProficiencies();

        // Document may reload, undoing the remove call above.
        setTimeout(
          () => { this.removeUnknownGrantedProficiencies(); },
          1000
        );
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingProficiencies = false; });
  }

  private removeUnknownGrantedProficiencies() {
    const profIds = new Set<number>();
    for (const prof of this.proficiencyList) {
      profIds.add(prof.id);
    }

    this.documentTemplate.granted_adult_proficiencies =
      this.documentTemplate.granted_adult_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_adult_non_participant_proficiencies =
      this.documentTemplate.granted_adult_non_participant_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_minor_proficiencies =
      this.documentTemplate.granted_minor_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_unsupervised_minor_proficiencies =
      this.documentTemplate.granted_unsupervised_minor_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_supervised_adult_proficiencies =
      this.documentTemplate.granted_supervised_adult_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_supervisor_proficiencies =
      this.documentTemplate.granted_supervisor_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_supervisee_proficiencies =
      this.documentTemplate.granted_supervisee_proficiencies
        .filter(pId => profIds.has(pId));
    this.documentTemplate.granted_climber_proficiencies =
      this.documentTemplate.granted_climber_proficiencies
        .filter(pId => profIds.has(pId));
  }

}
